import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { X } from "lucide-react";
import { toast } from "sonner";

interface AuthDebuggerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AuthDebugger = ({ isOpen, onClose }: AuthDebuggerProps) => {
  const [status, setStatus] = useState<string[]>([]);
  const [tokenDetails, setTokenDetails] = useState<string>("None");

  const addLog = (msg: string) => {
    setStatus(prev => [...prev, `${new Date().toISOString().split('T')[1].split('.')[0]} - ${msg}`]);
    console.log(`[AuthDebugger] ${msg}`);
  };

  const checkLocalStorage = () => {
    const keys = Object.keys(localStorage).filter(k => k.startsWith('sb-'));
    addLog(`Found ${keys.length} Supabase keys: ${keys.join(', ')}`);
    if (keys.length > 0) {
      const val = localStorage.getItem(keys[0]);
      setTokenDetails(val ? `${val.substring(0, 20)}...` : "Empty");
    } else {
      setTokenDetails("None");
    }
  };

  const testGetSession = async () => {
    addLog("Testing getSession()...");
    const start = Date.now();
    try {
      const { data, error } = await supabase.auth.getSession();
      const duration = Date.now() - start;
      addLog(`getSession finished in ${duration}ms`);
      if (error) addLog(`Error: ${error.message}`);
      else addLog(`Session user: ${data.session?.user?.id || 'null'}`);
    } catch (e) {
      addLog(`Exception: ${e}`);
    }
  };

  const forceRefresh = async () => {
    addLog("Forcing session refresh...");
    const { data, error } = await supabase.auth.refreshSession();
    if (error) addLog(`Refresh error: ${error.message}`);
    else addLog(`Refresh success: ${data.session?.user?.id}`);
  };

  useEffect(() => {
    if (isOpen) {
      checkLocalStorage();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Card className="fixed bottom-4 right-4 w-80 z-50 bg-background/95 border-2 border-red-500">
      <CardHeader className="py-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-mono text-red-500">Auth Debugger</CardTitle>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="py-2 space-y-2">
        <div className="text-xs font-mono bg-muted p-2 rounded h-32 overflow-y-auto">
          {status.map((s, i) => <div key={i}>{s}</div>)}
        </div>
        <div className="text-xs truncate">Token: {tokenDetails}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={checkLocalStorage}>Check Storage</Button>
          <Button size="sm" variant="outline" onClick={testGetSession}>Test Session</Button>
          <Button size="sm" variant="outline" onClick={forceRefresh}>Force Refresh</Button>
        </div>
      </CardContent>
    </Card>
  );
};

